import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";
import MenuService from "../../services/menu/MenuService";

import {
  setMenus,
  setMenuDetail,
  clearMenuDetail,
  resetMenuState,
  updateMenuNameSuccess,
  updateMenuRouteSuccess,
  updateMenuOrderSuccess,
  updateMenuRolesSuccess,
  updateMenuField,
} from "../../store/slices/menu/menuSlice";

export const useMenu = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { menus, menuDetail } = useSelector((state: RootState) => state.menu);

  // ===================================================
  // 🌐 CONSULTAS
  // ===================================================
  const fetchMenusByModule = async (moduleId: number) => {
    try {
      showLoadingIndicator();
      const response = await MenuService.getMenusByModule(moduleId);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && Array.isArray(data)) {
        dispatch(setMenus(data)); // Lista de menús (IMenuSummary[])
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const fetchMenuDetail = async (menuId: number) => {
    try {
      showLoadingIndicator();
      const response = await MenuService.getMenuDetail(menuId);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setMenuDetail(data)); // Detalle de un solo menú (IMenuDetail)
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const clearDetail = () => dispatch(clearMenuDetail());
  const reset = () => dispatch(resetMenuState());

  // ===================================================
  // ✏️ ACTUALIZACIONES (backend)
  // ===================================================
  const updateMenuName = async (payload: { menuId: number; newName: string }) => {
    try {
      showLoadingIndicator();
      const response = await MenuService.updateMenuName(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateMenuNameSuccess({ newName: data }));
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateMenuRoute = async (payload: { menuId: number; newRoute: string }) => {
    try {
      showLoadingIndicator();
      const response = await MenuService.updateMenuRoute(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateMenuRouteSuccess({ newRoute: data }));
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateMenuOrder = async (payload: { menuId: number; newOrder: number }) => {
    try {
      showLoadingIndicator();
      const response = await MenuService.updateMenuOrder(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateMenuOrderSuccess({ newOrder: data }));
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateMenuRoles = async (payload: { menuId: number; roleIds: number[] }) => {
    try {
      showLoadingIndicator();
      const response = await MenuService.updateMenuRoles(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && Array.isArray(data)) {
        dispatch(updateMenuRolesSuccess({ roles: data }));
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // ===================================================
  // 🧩 ACTUALIZACIÓN LOCAL (sin backend)
  // ===================================================
  const updateFieldLocally = (field: keyof typeof menuDetail, value: any) => {
    if (!menuDetail) return;
    dispatch(updateMenuField({ field, value }));
  };

  // ===================================================
  // 📦 Retorno del hook
  // ===================================================
  return {
    menus,
    menuDetail,

    // Fetchers
    fetchMenusByModule,
    fetchMenuDetail,

    // Estado
    clearDetail,
    reset,

    // Mutaciones
    updateMenuName,
    updateMenuRoute,
    updateMenuOrder,
    updateMenuRoles,

    // Local
    updateFieldLocally,
  };
};

export default useMenu;
